import React, { useEffect, useState } from 'react';
import { GraduationCap, Plus, Pencil, Trash2, Loader2, Save, X, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const DEFAULT_CATEGORIES = [
  'Generale', 'Biens', 'Clients', 'Paiements', 'RH', 'Construction',
  'Locataires', 'Proprietaires', 'Comptabilite',
];

const emptyForm = () => ({
  id: null,
  title: '',
  category: 'Generale',
  description: '',
  steps: [''],
  video_url: '',
  shortcuts: [],
});

const Training = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/modules');
      setModules(r.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => setEditing(emptyForm());
  const startEdit = (m) => setEditing({
    ...emptyForm(),
    ...m,
    steps: m.steps && m.steps.length ? m.steps : [''],
    shortcuts: m.shortcuts || [],
    video_url: m.video_url || '',
  });

  const cancelEdit = () => setEditing(null);

  const setStep = (i, val) => {
    const next = [...editing.steps];
    next[i] = val;
    setEditing({ ...editing, steps: next });
  };
  const addStep = () => setEditing({ ...editing, steps: [...editing.steps, ''] });
  const removeStep = (i) => {
    const next = editing.steps.filter((_, idx) => idx !== i);
    setEditing({ ...editing, steps: next.length ? next : [''] });
  };

  const setShortcut = (i, key, val) => {
    const next = [...editing.shortcuts];
    next[i] = { ...next[i], [key]: val };
    setEditing({ ...editing, shortcuts: next });
  };
  const addShortcut = () => setEditing({ ...editing, shortcuts: [...editing.shortcuts, { label: '', path: '' }] });
  const removeShortcut = (i) => setEditing({ ...editing, shortcuts: editing.shortcuts.filter((_, idx) => idx !== i) });

  const handleSave = async () => {
    if (!editing.title.trim()) return toast.error('Titre obligatoire');
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        category: (editing.category || 'Generale').trim(),
        description: editing.description || '',
        steps: editing.steps.map(s => s.trim()).filter(Boolean),
        video_url: editing.video_url || null,
        shortcuts: editing.shortcuts
          .map(s => ({ label: (s.label || '').trim(), path: (s.path || '').trim() }))
          .filter(s => s.label && s.path),
      };
      if (editing.id) {
        await api.put(`/training/modules/${editing.id}`, payload);
        toast.success('Module mis a jour');
      } else {
        await api.post('/training/modules', payload);
        toast.success('Module cree');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Supprimer la formation "${m.title}" ?`)) return;
    try {
      await api.delete(`/training/modules/${m.id}`);
      toast.success('Module supprime');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  // Group by category
  const grouped = modules.reduce((acc, m) => {
    const cat = m.category || 'Generale';
    acc[cat] = acc[cat] || [];
    acc[cat].push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="training-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1B3A4B] flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-[#D4A745]" />
            Formation
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Bibliotheque de fiches de formation pour les agents. {isAdmin && 'Vous pouvez les utiliser pendant un appel video pour guider un collaborateur.'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={startCreate} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="add-training-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle formation
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#D4A745]" /></div>
      ) : modules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <GraduationCap className="w-12 h-12 mx-auto opacity-30 mb-3" />
            <p className="font-medium">Aucune formation pour l'instant.</p>
            {isAdmin && (
              <p className="text-sm mt-1">Cliquez sur "Nouvelle formation" pour creer la premiere fiche.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{cat}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((m) => (
                  <Card key={m.id} className="hover:shadow-md transition" data-testid={`module-card-${m.id}`}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{m.title}</h3>
                          <Badge variant="outline" className="text-[10px] mt-1">{m.category}</Badge>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(m)} data-testid={`edit-${m.id}`}>
                              <Pencil className="w-3.5 h-3.5 text-blue-600" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDelete(m)} data-testid={`delete-${m.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-600" />
                            </Button>
                          </div>
                        )}
                      </div>
                      {m.description && (
                        <p className="text-xs text-gray-600 line-clamp-2">{m.description}</p>
                      )}
                      <div className="text-xs text-gray-500 flex flex-wrap gap-3 pt-1">
                        {m.steps?.length > 0 && <span>{m.steps.length} etape{m.steps.length > 1 ? 's' : ''}</span>}
                        {m.video_url && <span className="text-violet-600">video</span>}
                        {m.shortcuts?.length > 0 && <span>{m.shortcuts.length} raccourci{m.shortcuts.length > 1 ? 's' : ''}</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && cancelEdit()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-[#D4A745]" />
              {editing?.id ? 'Modifier la formation' : 'Nouvelle formation'}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Titre *</Label>
                  <Input
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    placeholder="Ex : Comment enregistrer un paiement de loyer"
                    data-testid="training-title-input"
                  />
                </div>
                <div>
                  <Label>Categorie</Label>
                  <Input
                    list="training-cat-list"
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  />
                  <datalist id="training-cat-list">
                    {DEFAULT_CATEGORIES.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div>
                  <Label>Lien video (YouTube, Loom...)</Label>
                  <Input
                    value={editing.video_url || ''}
                    onChange={(e) => setEditing({ ...editing, video_url: e.target.value })}
                    placeholder="https://youtu.be/..."
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  rows={3}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Resume bref de ce que cette formation explique."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Etapes</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addStep}>
                    <Plus className="w-3 h-3 mr-1" /> Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {editing.steps.map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="text-sm font-semibold text-gray-500 mt-2 w-6">{i + 1}.</span>
                      <Textarea
                        rows={2}
                        value={s}
                        onChange={(e) => setStep(i, e.target.value)}
                        placeholder={`Etape ${i + 1}`}
                        className="flex-1"
                      />
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeStep(i)} className="h-9 w-9 mt-1">
                        <X className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Raccourcis (page interne)</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addShortcut}>
                    <Plus className="w-3 h-3 mr-1" /> Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {editing.shortcuts.map((sc, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2">
                      <Input
                        value={sc.label || ''}
                        onChange={(e) => setShortcut(i, 'label', e.target.value)}
                        placeholder="Libelle (ex: Ouvrir Biens)"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={sc.path || ''}
                          onChange={(e) => setShortcut(i, 'path', e.target.value)}
                          placeholder="/properties"
                          className="flex-1"
                        />
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeShortcut(i)} className="h-9 w-9">
                          <X className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={cancelEdit}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="save-training-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Training;
